import {
  AnalyticQueuesResponse,
  AnalyticsDataResponse,
  AnalyticsDetailsResponse
} from "@queries/Analytics";
import { ANALYTICS_CHART_TYPE, SERIES_Z_VALUE } from "@resources/Constants";
import { numberSymbol } from "@utils/Widget";
import {
  EChartsOption,
  TooltipComponentFormatterCallbackParams
} from "echarts";
import _, { isArray } from "lodash";
import { DefaultTheme } from "styled-components";
import { theme as HVTheme } from "@repo/HVComponents";
import { commonTooltipSettings } from "@components/report/widget/widget-statistics/helper";
import { BaseTimeProps } from "../Interfaces";

const tooltipSetting = (
  data: { value: number; intervalName: string; idx: number }[],
  isHourCategory: boolean
): EChartsOption["tooltip"] => ({
  trigger: "axis",
  backgroundColor: commonTooltipSettings().backgroundColor,
  borderColor: commonTooltipSettings().borderColor,
  borderRadius: commonTooltipSettings().borderRadius,
  textStyle: {
    fontSize: 12,
    fontFamily: HVTheme.fontFamily,
    color: commonTooltipSettings().textStyle.color
  },
  padding: commonTooltipSettings().padding,
  formatter: (params: TooltipComponentFormatterCallbackParams) => {
    if (isArray(params)) {
      const tooltipDetail = data.find((item) =>
        isHourCategory
          ? item.idx === params[0].dataIndex
          : item.intervalName.slice(0, 3) === params[0].name
      );
      return `<div class="widget-tooltip" data-testid="widget-tooltip" style="display: flex; flex-direction: column; width: max-content;">
        <div style="font-size: 12px; font-weight: bold; line-height: 16px;">${tooltipDetail?.intervalName}</div>
        <div data-testid="widget-tooltip-item-label" style="display: flex; gap: 4px; align-items: center;">
                      ${
                        params[0].color
                          ? `<span style="width: 6px;height: 6px; border-radius: 50%; background-color: ${params[0].color};"></span>`
                          : ""
                      }
                      <span">${tooltipDetail?.value}</span>
                    </div>
      </div>`;
    }
    return "";
  }
});

export const prepareOptions = (
  labels: string[],
  data: { value: number; intervalName: string; idx: number }[],
  theme: DefaultTheme,
  isHourCategory: boolean
) => ({
  tooltip: tooltipSetting(data, isHourCategory),
  grid: {
    left: 22,
    right: 35,
    bottom: 20,
    top: 7,
    containLabel: true
  },
  xAxis: {
    type: "category",
    name: isHourCategory ? "Timeline" : "Day of the week",
    nameTextStyle: {
      fontFamily: HVTheme.fontFamily,
      fontWeight: 400,
      fontSize: "12px",
      lineHeight: 12,
      letterSpacing: "0%",
      textAlign: "center",
      color: theme.chart.colorNewText
    },
    nameLocation: "middle",
    data: labels,
    nameGap: 25,
    axisLabel: {
      color: theme.chart.colorNewAxisLabel,
      fontSize: "12px",
      fontFamily: HVTheme.fontFamily,
      hideOverlap: false
    },
    axisLine: {
      show: true,
      onZero: false,
      lineStyle: {
        color: theme.widget.entranceExitPeopleCounts.graphLineDefault,
        width: 1
      }
    },
    axisTick: {
      lineStyle: {
        color: theme.widget.entranceExitPeopleCounts.graphLineDefault,
        width: 1
      }
    }
  },
  yAxis: {
    type: "value",
    name: "Average count",
    nameTextStyle: {
      fontFamily: HVTheme.fontFamily,
      fontWeight: 400,
      fontSize: "12px",
      lineHeight: 12,
      letterSpacing: "0%",
      textAlign: "center",
      color: theme.chart.colorNewText
    },
    nameLocation: "middle",
    nameGap: 35,
    axisLabel: {
      color: theme.chart.colorNewAxisLabel,
      fontSize: "12px",
      fontFamily: HVTheme.fontFamily,
      hideOverlap: false,
      formatter: (value: number) => {
        const [number, symbol] = numberSymbol(value);
        return value < SERIES_Z_VALUE.ORIGINAL_DATA
          ? `${number.padStart(3, " 0")}${symbol}`
          : `${number}${symbol}`;
      }
    },
    splitLine: {
      show: true,
      lineStyle: {
        color: theme.chart.splitLineColor,
        width: 1
      }
    }
  },
  series: [
    {
      data: data.map((item) => item.value),
      type: "bar",
      barWidth: "8px",
      itemStyle: {
        color: theme.chart.analyticsBarChartColor
      }
    }
  ]
});

export const categoryTypes = {
  BY_HOURS: "HOUR",
  BY_DAY_OF_WEEKS: "DAY"
};

export const getHighestTotal = (
  data: AnalyticsDetailsResponse[],
  type: string,
  baseTime: BaseTimeProps
): Partial<AnalyticsDataResponse> & { time?: number } => {
  if (
    [
      ANALYTICS_CHART_TYPE.PEOPLE_QUEUES,
      ANALYTICS_CHART_TYPE.RETAIL_VISITOR_AREA_TRAFFIC,
      ANALYTICS_CHART_TYPE.SHOPPING_CART_QUEUES,
      ANALYTICS_CHART_TYPE.VEHICLE_QUEUES
    ].includes(type)
  ) {
    const highest = _.maxBy(data, "averageQueueCount");

    return {
      locationName: highest?.locationName,
      cameraName: highest?.cameraName,
      total: Number(
        ((highest as AnalyticQueuesResponse)?.averageQueueCount || 0).toFixed(1)
      ),
      time: Number(
        ((highest as AnalyticQueuesResponse)?.averageDwellTime || 0).toFixed(0)
      )
    };
  }
  if (
    [
      ANALYTICS_CHART_TYPE.PEOPLE_OCCUPANCY,
      ANALYTICS_CHART_TYPE.VEHICLE_OCCUPANCY,
      ANALYTICS_CHART_TYPE.SHOPPING_CARTS_IN_USE
    ].includes(type)
  ) {
    const highest = _.maxBy(data, "total");

    const daysDifference = Math.max(
      1,
      Math.ceil(
        (new Date(baseTime?.end || 0).getTime() -
          new Date(baseTime?.start || 0).getTime()) /
          (1000 * 60 * 60 * 24)
      )
    );

    const highestAverage =
      ((highest as AnalyticsDataResponse)?.total ?? 0) / daysDifference;

    return {
      locationName: highest?.locationName || "",
      total: Number(highestAverage.toFixed(1))
    };
  }
  const maxLocationData = (data as AnalyticsDataResponse[]).reduce(
    (prev, current) => {
      if (prev.total !== undefined && current.total !== undefined) {
        return prev.total > current.total ? prev : current;
      }
      return prev.total !== undefined ? prev : current;
    }
  );

  return maxLocationData;
};
